import React, {useState} from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useMutation} from '@apollo/client';
import {CREATE_ORGANIZATION} from '../graphql/mutations';
import {useOrg} from '../contexts/OrgContext';
import {auth} from '../config/firebase';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

export default function CreateOrgPage() {
  useDocumentTitle('Create Organization');

  const {refetchOrgs} = useOrg();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');

  const [createOrg, {loading: creating}] = useMutation(CREATE_ORGANIZATION);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      const token = await auth.currentUser?.getIdToken();
      await createOrg({
        variables: {name: name.trim(), ...(website.trim() ? {website: website.trim()} : {})},
        context: {headers: {authorization: `Bearer ${token}`}},
      });
      refetchOrgs();
      navigate('/org/guides');
    } catch (err: any) {
      setError(err.message || 'Failed to create organization');
    }
  };

  return (
    <Box sx={{flex: 1, bgcolor: 'background.default'}}>
      <Container maxWidth="sm" sx={{py: 4}}>
        <Typography variant="h4" component="h1" sx={{fontWeight: 500, mb: 1}}>
          Create Organization
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{mb: 4}}>
          Set up your organization to start creating guides.
        </Typography>

        {error && (
          <Alert severity="error" sx={{mb: 3}}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Organization Name"
              placeholder="e.g. Seattle Public Schools"
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth
              required
              autoFocus
            />

            <TextField
              label="Website"
              placeholder="e.g. https://www.example.com"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              fullWidth
              type="url"
              helperText="Your organization's website URL (optional). Used to generate a URL slug."
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={creating || !name.trim()}
            >
              {creating ? 'Creating...' : 'Create Organization'}
            </Button>
          </Stack>
        </form>
      </Container>
    </Box>
  );
}
